import {
  Component,
  Host,
  h,
  State,
  Event,
  Listen,
  EventEmitter,
  Prop,
} from '@stencil/core';
import {
  BasketRepository,
  BasketModel,
  BasketService,
  ExtraHelper,
  ExtraRepository,
  ExtraService,
  ExtraStructure,
  SessionRepository,
  SessionService,
  Language,
} from '@cesare';
import {
  first,
  switchMap,
  distinctUntilChanged,
  map,
  debounceTime,
  of,
  zip,
  combineLatest,
} from 'rxjs';
import { QwSessionSettings } from '../../index';
import { QwExtraEmitter } from './qw-extra-card/qw-extra-card';
import { isEqual } from 'lodash-es';
import { getSessionSettingsFromSessionStorage } from '../../core/storage/storage.service';
import { loadingService } from '../../core/qw-loading-manager/qw-loading.service';
import { roundPriceToTwoDecimals, getCurrencyText } from '../../core/utils';

@Component({
  tag: 'qw-extra',
  styleUrl: 'qw-extra.scss',
  shadow: false,
})
export class QwExtra {
  @Prop() currentLang: string;
  @State() extra: ExtraStructure;
  @State() basket: BasketModel;
  @State() canAddMoreExtra: boolean;
  @State() sessionSettings: QwSessionSettings;
  @State() componentLoading: boolean = true;
  @State() renderTriggered: boolean = false;
  @Event() qwExtrasLoaded: EventEmitter<ExtraStructure>;

  public componentWillLoad() {
    SessionService.getSession().pipe(distinctUntilChanged(isEqual)).subscribe();
    SessionRepository.sessionLoaded$
      .pipe(
        switchMap((session) =>
          zip(
            of(session.sessionId),
            SessionRepository.sessionHasRooms$,
            BasketService.getBasket(session),
          ),
        ),
        switchMap(([sessionId, hasRooms]) =>
          hasRooms ? ExtraService.getExtra(sessionId) : of(null),
        ),
        distinctUntilChanged(isEqual),
      )
      .subscribe();

    ExtraRepository.extraLoaded$
      .pipe(distinctUntilChanged(isEqual))
      .subscribe((extra) => {
        this.extra = extra;
        this.qwExtrasLoaded.emit(extra);
      });
    BasketRepository.basketWithPrice$
      .pipe(distinctUntilChanged(isEqual))
      .subscribe((basket) => {
        this.basket = basket;
      });

    this.componentIsLoading$.pipe(distinctUntilChanged()).subscribe((value) => {
      this.componentLoading = value;
      this.setServiceLoading(this.componentLoading);
    });

    this.setServiceLoading(true);

    loadingService.loadComplete$.subscribe((res) => {
      this.renderTriggered = res;
    });

    this.sessionSettings = getSessionSettingsFromSessionStorage();
  }

  public componentIsLoading$ = combineLatest([
    ExtraRepository.extraIsLoading$,
    BasketRepository.basketIsLoading$,
    ExtraRepository.extraLoaded$,
    BasketRepository.basketWithPrice$,
    SessionRepository.sessionLoaded$,
  ]).pipe(
    map(
      ([
        extraLoading,
        basketLoading,
        extraLoaded,
        basketWithPrice,
        sessionLoaded,
      ]) => {
        return (
          extraLoading ||
          (basketLoading &&
            !(!!extraLoaded && !!basketWithPrice && !!sessionLoaded))
        );
      },
    ),
    debounceTime(0),
    distinctUntilChanged(),
  );

  private setServiceLoading(isLoading: boolean) {
    loadingService.setLoading({
      idComponent: 'extras',
      isLoading,
    });
  }

  @Listen('qwExtraCounterChanged')
  public extraChanged(e: CustomEvent<QwExtraEmitter>) {
    BasketService.setExtraInBasket({
      quantity: e.detail.quantity,
      roomId: e.detail.roomId,
      extraId: e.detail.extraId,
    })
      .pipe(first())
      .subscribe();
  }

  @Listen('qwSingleExtraChanged')
  public singleExtraChanged(e: CustomEvent<QwExtraEmitter>) {
    BasketService.setExtraInBasket({
      quantity: e.detail.quantity,
      roomId: e.detail.roomId,
      extraId: e.detail.extraId,
    })
      .pipe(first())
      .subscribe();
  }

  @Listen('qwQuantityExtraChanged')
  public quantityExtraChanged(e: CustomEvent<QwExtraEmitter>) {
    loadingService.setLoading({
      idComponent: `extra-changed`,
      isLoading: true,
    });
    BasketService.setExtraInBasket({
      quantity: e.detail.quantity,
      roomId: e.detail.roomId,
      extraId: e.detail.extraId,
    })
      .pipe(first())
      .subscribe(() => {
        loadingService.setLoading({
          idComponent: `extra-changed`,
          isLoading: false,
        });
        loadingService.setLoading({
          idComponent: `basket-summary`,
          isLoading: false,
        });
        loadingService.setLoading({
          idComponent: `basket-summary-mobile`,
          isLoading: false,
        });
      });
  }

  private isInitData() {
    return this.extra && this.basket;
  }

  private getSummaryExtra(extra) {
    return (
      extra.summary.length && (
        <p innerHTML={extra.summary.find((s) => s.text).text}></p>
      )
    );
  }

  private getMaxAvailability(items) {
    return Math.max.apply(
      Math,
      items.map((i) => i.quantity.value),
    );
  }

  renderSkeleton() {
    const dataExtraSkeleton = [
      {
        qwRepeat: 1,
        qwWidth: '65px',
        qwHeight: '65px',
        qwType: 'image',
      },
      {
        qwRepeat: 1,
        qwWidth: '40%',
        qwHeight: '28px',
        qwType: 'title',
      },
      {
        qwRepeat: 2,
        qwWidth: '80%',
        qwHeight: '24px',
        qwType: 'text',
      },
      {
        qwRepeat: 1,
        qwWidth: '100%',
        qwHeight: '35px',
        qwType: 'select',
      },
    ];
    return (
      <Host>
        <div class="grid-skeleton">
          <qw-skeleton class="card" qwData={dataExtraSkeleton}></qw-skeleton>
          <qw-skeleton class="card" qwData={dataExtraSkeleton}></qw-skeleton>
          <qw-skeleton class="card" qwData={dataExtraSkeleton}></qw-skeleton>
        </div>
      </Host>
    );
  }

  private getExtraPriceFormatted(extra) {
    let priceAmount = extra.price.unitPrice.converted.value.amount;
    let currency = extra.price.unitPrice.converted.value.currency;
    return `${getCurrencyText(currency) || currency} ${
      roundPriceToTwoDecimals(priceAmount) || extra.gratuitousnessType.text
    }`;
  }

  private getExtraPriceDiscountedFormatted(extra) {
    let priceAmount = extra.price.crossedOutPrice.converted.value.amount;
    let currency = extra.price.crossedOutPrice.converted.value.currency;
    if (priceAmount == 0 || currency == null) return '';

    return `${getCurrencyText(currency) || currency} ${
      roundPriceToTwoDecimals(priceAmount) || extra.gratuitousnessType.text
    }`;
  }

  renderComponent() {
    const hasExtra =
      (this.extra.hotelExtras.length > 0 ||
        this.extra.hotelExtras.length === 0) &&
      this.basket;
    const noExtraOnBackOffice =
      this.extra.hotelExtras.length === 0 && this.basket;
    return (
      <Host
        class={
          //  ${(this.extra.hotelExtras.length === 0 && !this.basket) ? 'qw-extra--loading' : 'qw-extra--loaded'}
          `${this.componentLoading ? 'loading-skeleton' : ''}
        `
        }
      >
        {/* {noExtraAndRooms && <p class="qw-extra__warning">Before choosing extras, please add rooms to basket.</p>} */}
        {!hasExtra && this.componentLoading && <div class="qw-loader"></div>}
        {noExtraOnBackOffice && !this.componentLoading && (
          <p class="qw-extra__warning">
            {Language.getTranslation('extraNotAvailable')}
          </p>
        )}
        {this.extra.hotelExtras.length > 0 && (
          <div class="qw-extra__card-wrapper">
            {/* <h3 class="qw-extra__card-wrapper-title">{Language.getTranslation('extras')}</h3> */}
            <div class="qw-extra__card-content">
              {this.isInitData() &&
                this.extra.hotelExtras.map((extra) => {
                  const basketExtra = this.basket.hotelExtras.find(
                    (basketEx) => basketEx.extraId === extra.extraId,
                  );
                  return (
                    <qw-extra-card
                      class={
                        this.componentLoading ? 'qw-extra-card--disabled' : ''
                      }
                      qwExtraCardId={extra.extraId}
                      qwExtraCardName={extra.name}
                      qwExtraCardSummary={this.getSummaryExtra(extra)}
                      qwExtraCardCover={ExtraHelper.getCoverImage(extra).url}
                      qwExtraCardCounting={extra.counting}
                      qwExtraCardUnitQuantity={extra.items[0].quantity.value}
                      qwExtraCardQuantityOptions={extra.items}
                      qwExtraCardUnitPrice={this.getExtraPriceFormatted(extra)}
                      qwExtraCardUnitPriceDiscounted={this.getExtraPriceDiscountedFormatted(
                        extra,
                      )}
                      qwExtraCardAvailability={this.getMaxAvailability(
                        extra.items,
                      )}
                      qwExtraCardCanAddMoreExtra={
                        basketExtra?.selectedQuantity.value > 0
                      }
                      qwExtraCardShowCounter={false}
                      qwExtraCardSelectedQuantityValue={
                        basketExtra ? basketExtra.selectedQuantity.value : 0
                      }
                    />
                  );
                })}
            </div>
          </div>
        )}
        {this.basket &&
          this.basket.rooms.map((r) => {
            return (
              <div class="qw-extra__card-wrapper">
                {this.extra.roomExtras[r.roomId] && (
                  <div class="qw-extra__card-content">
                    {this.isInitData() &&
                      this.extra.roomExtras[r.roomId]?.map((e) => {
                        const extra = e;
                        const basketExtra = r.extras[e.extraId];
                        return (
                          <qw-extra-card
                            class={
                              this.componentLoading
                                ? 'qw-extra-card--disabled'
                                : ''
                            }
                            qwExtraCardRoomId={r.roomId}
                            qwExtraCardId={extra.extraId}
                            qwExtraCardName={extra.name}
                            qwExtraCardSummary={this.getSummaryExtra(extra)}
                            qwExtraCardCover={
                              ExtraHelper.getCoverImage(extra).url
                            }
                            qwExtraCardCounting={extra.counting}
                            qwExtraCardUnitQuantity={
                              extra.items[0].quantity.value
                            }
                            qwExtraCardQuantityOptions={extra.items}
                            qwExtraCardUnitPrice={this.getExtraPriceFormatted(
                              extra,
                            )}
                            qwExtraCardUnitPriceDiscounted={this.getExtraPriceDiscountedFormatted(
                              extra,
                            )}
                            qwExtraCardAvailability={this.getMaxAvailability(
                              extra.items,
                            )}
                            qwExtraCardCanAddMoreExtra={
                              basketExtra?.selectedQuantity.value > 0
                            }
                            qwExtraCardShowCounter={false}
                            qwExtraCardSelectedQuantityValue={
                              basketExtra
                                ? basketExtra.selectedQuantity.value
                                : 0
                            }
                          />
                        );
                      })}
                  </div>
                )}
              </div>
            );
          })}
      </Host>
    );
  }

  render() {
    return this.renderTriggered
      ? this.renderComponent()
      : this.renderSkeleton();
  }
}
